<template>
  <a-config-provider :locale="locale">
    <div :class="{watermark: appEnv, 'ios-device': isIosMobile, 'android-device': isAndroidMobile, 'mobile-device': isMobile, landscape: isLandscape, portrait: isPortrait}">
      <span
        class="env-bandage"
        @dblclick="checkVersion"
      >
        {{ appEnv }}
      </span>
      <div
        class="app-container"
        :class="{noBorder: !showHeaderFooter}"
      >
        <AppHeader
          v-show="showHeaderFooter"
          ref="appHeader"
        />
        <div class="app-body-container row">
          <div class="medium-12 small-12 large-12 columns">
            <transition
              name="router-fade"
              mode="out-in"
            >
              <router-view class="app-body" />
            </transition>
          </div>
        </div>

        <loading ref="loadingLayer" />
        <no-perm-modal ref="noPermModal" />
        <extend-session-modal ref="extendSessionPermModal" />
        <idle-time-check-modal ref="extendSessionPermModal" />
        <sticky-footer v-if="showHeaderFooter" />
      </div>
    </div>
  </a-config-provider>
</template>

<script>
import HttpStatus from 'http-status-codes'
import lodash from 'lodash'
import moment from 'moment'
import { ref, watch } from 'vue-demi'
import * as app from '@/api/apps/request'
import { checkStatus } from '@/api/users/request'
import ExtendSessionModal from '@/modules/ExtendSessionModal'
import StickyFooter from '@/modules/Footer'
import AppHeader from '@/modules/Header'
import IdleTimeCheckModal from '@/modules/IdleTimeCheckModal'
import Loading from '@/modules/Loading'
import NoPermModal from '@/modules/NoPermModal'
import EventBus from '@/utils/eventBus'
import i18n from '@/utils/i18n'
import notification from '@/utils/notification'
import userHelper from '@/utils/userHelper'
import { useAntdLocale } from './i18n/antd.locale.hook'
import useRouter from './router/useRouter'
import { useStoreGetters } from './store/index'

export default {
  name: 'App',
  components: {
    AppHeader,
    StickyFooter,
    Loading,
    NoPermModal,
    ExtendSessionModal,
    IdleTimeCheckModal,
  },
  setup () {
    const {
      isAndroidMobile,
      isIosMobile,
      isMobile,
      isLandscape,
      isPortrait,
    } = userHelper.useMobileUA()
    const { route } = useRouter()
    const showHeaderFooter = ref(false)
    const { currentLanguage } = useStoreGetters('language', ['currentLanguage'])
    const locale = useAntdLocale(currentLanguage)
    watch([route], () => {
      showHeaderFooter.value = lodash.get(route, 'value.meta.showHeaderFooter', true)
    })
    return {
      locale,
      isAndroidMobile,
      isIosMobile,
      isMobile,
      isLandscape,
      isPortrait,
      showHeaderFooter,
    }
  },
  data () {
    return {
      startCheckResult: null,
      checkExpireInterval: 30000,
      appEnv: '',
      shouldUpdateSid: false,
    }
  },
  async mounted () {
    if (!this.appEnv) {
      try {
        const { envName } = await app.getApiVer()
        this.appEnv = envName
      } catch (e) {
        console.error('Fail to call api version:', e)
      }
    }

    EventBus.on('apps_start_check_expire', () => {
      this.startCheckExpire()
    })
    EventBus.on('apps_stop_check_expire', () => {
      this.stopCheckExpire()
    })
    EventBus.on('http_request_time_out', () => {
      notification.fireNotification(false, i18n.t('error.408'))
    })
    const setLiveStatus = lodash.throttle(() => { this.shouldUpdateSid = true }, 1000)
    window.addEventListener('load', setLiveStatus)
    window.addEventListener('mousemove', setLiveStatus)
    window.addEventListener('touchstart', setLiveStatus)
    window.addEventListener('keypress', setLiveStatus)
    window.addEventListener('scroll', setLiveStatus)
    // add some navigator info in html tag
    document.documentElement.setAttribute('data-platform', navigator.platform)
  },
  destroyed () {
    EventBus.$off('apps_start_check_expire')
    EventBus.$off('apps_stop_check_expire')
  },
  methods: {
    async checkExpire () {
      try {
        await checkStatus(this.shouldUpdateSid)
      } catch (err) {
        const status = deep(err, 'response.status')
        if (status === HttpStatus.UNAUTHORIZED) {
          EventBus.$emit('modules_extend_session_modal_close')
          notification.warn(i18n.t('hint.sessionExpireMessage'))
        } else if (status === HttpStatus.REQUEST_TIMEOUT) {
          EventBus.$emit('modules_extend_session_modal_open')
        }
      } finally {
        this.shouldUpdateSid = false
      }
    },
    startCheckExpire () {
      if (!this.startCheckResult) {
        this.startCheckResult = setInterval(this.checkExpire, this.checkExpireInterval)
      }
    },
    stopCheckExpire () {
      clearInterval(this.startCheckResult)
      this.startCheckResult = null
    },
    checkVersion () {
      const dataset = document.body.dataset
      const buildAt = dataset.buildAt.replace(/\s*-\s*/, '\n')
        .replace(/\d+/g, m => `built at ${moment(+m).format('HH:mm:ss YYYY-MM-DD')}`)
      alert(`${dataset.ver}\n${buildAt}`)
    },
  },
}
</script>

<style lang="scss">
@import '~styles/styles.scss';
@import "~styles/common";
@import "~styles/common-custom";

.app-body {
  min-height: calc(100vh - 126px);
  position: relative;
}
.app-body-container{
  min-height: calc(100vh - 126px);
  & > div.columns {
    border-bottom: none;
    border-top: none;
    border-left: solid 1px grey;
    border-right: solid 1px grey;
  }

}
.overlay {
  pointer-events: none!important;
}

.app-container {
  margin: 0 auto;
  min-width: 480px;
}

.noBorder{
  border: none;
}

@media print {
  .app-container {
    margin: 0 auto;
    border: none;
  }
  .large-12 {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

@page {
  size: auto;   /* auto is the initial value */

  /* this affects the margin in the printer settings */
  margin-top: 5mm;
  margin-bottom: 5mm;
  margin-left: 1.82mm;
  margin-right: 1.82mm
}

.env-bandage {
  display: none;
  height: 66px;
  padding-top: 24px;
  color: #E49405;
  font-size: 22px;
  text-transform: uppercase;
  user-select: none;
}

.watermark  .env-bandage{
  display: inline-block;
  position: absolute;
  left: 250px;
}
</style>
